// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import './styles.css';
import { openAbout } from './components/about';
import { openQuakeDrawer } from './components/detail';
import { initOverlays } from './components/overlays';
import { parsePlates } from './geo';
import { num, relTime } from './format';
import { initTooltip } from './tooltip';
import { fetchFeed } from './usgs';
import { savePrefs, state } from './state';
import type { MagBand, Period } from './types';
import { renderDepth } from './views/depth';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { renderMagnitude } from './views/magnitude';
import { renderMap } from './views/map';
import { renderOverview } from './views/overview';
import { renderRegions } from './views/regions';
import { renderTimeline } from './views/timeline';

interface ViewDef { label: string; render: (root: HTMLElement) => void | Promise<void> }

const VIEWS: Record<string, ViewDef> = {
  overview: { label: 'Overview', render: renderOverview },
  map: { label: 'Live Map', render: renderMap },
  magnitude: { label: 'Magnitude', render: renderMagnitude },
  depth: { label: 'Depth', render: renderDepth },
  regions: { label: 'Regions', render: renderRegions },
  timeline: { label: 'Timeline', render: renderTimeline },
  explorer: { label: 'Explorer', render: renderExplorer },
  insights: { label: 'Insights', render: renderInsights },
};

const PERIODS: Array<{ id: Period; label: string }> = [
  { id: 'hour', label: 'Hour' }, { id: 'day', label: 'Day' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' },
];
const BANDS: Array<{ id: MagBand; label: string }> = [
  { id: 'all', label: 'All' }, { id: '2.5', label: 'M2.5+' }, { id: '4.5', label: 'M4.5+' }, { id: 'significant', label: 'Significant' },
];

let controller: AbortController | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function shell(): string {
  return `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <svg class="brand-mark" viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="32" r="20" fill="none" stroke="#1f3350" stroke-width="2"/><path d="M6 34 H20 L24 20 L30 46 L36 12 L41 38 L45 30 H58" fill="none" stroke="#f97316" stroke-width="3.4" stroke-linejoin="round" stroke-linecap="round"/><circle cx="32" cy="32" r="3.2" fill="#f43f5e"/></svg>
          <div style="min-width:0">
            <div class="brand-name">Quake<span>Watch</span></div>
            <div class="brand-tag">Live global earthquake monitor</div>
          </div>
        </div>
        <div class="header-status">
          <span class="live-pill"><span class="live-dot loading" id="live-dot"></span><span class="live-count" id="live-count">Loading…</span></span>
          <button class="icon-btn" id="btn-refresh" aria-label="Refresh now" title="Refresh now">↻</button>
          <button class="icon-btn" id="btn-about" aria-label="About QuakeWatch" title="About">?</button>
        </div>
      </div>
      <div class="controls">
        <div class="control-group">
          <span class="control-label">Window</span>
          <div class="segmented" id="seg-period">${PERIODS.map((p) => `<button data-period="${p.id}" class="${state.period === p.id ? 'active' : ''}">${p.label}</button>`).join('')}</div>
        </div>
        <div class="control-group">
          <span class="control-label">Min magnitude</span>
          <div class="segmented" id="seg-band">${BANDS.map((b) => `<button data-band="${b.id}" class="${state.band === b.id ? 'active' : ''}">${b.label}</button>`).join('')}</div>
        </div>
      </div>
      <nav class="nav-tabs" id="nav-tabs" aria-label="Views">
        ${Object.entries(VIEWS).map(([id, v]) => `<button data-view="${id}" class="${state.view === id ? 'active' : ''}">${v.label}</button>`).join('')}
      </nav>
    </header>
    <main class="main-content"><div id="view-root"></div></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-sources">
          Data: <a href="https://earthquake.usgs.gov/" target="_blank" rel="noopener">USGS Earthquake Hazards Program</a> (public domain) ·
          Plate boundaries: Bird (2003) PB2002 · Basemap © CARTO / © OpenStreetMap.
          Preliminary data, revised over time. Not an emergency service.
        </div>
        <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
      </div>
    </footer>`;
}

function updateStatus(): void {
  const dot = document.getElementById('live-dot');
  const count = document.getElementById('live-count');
  if (!dot || !count) return;
  dot.className = `live-dot ${state.status === 'error' ? 'error' : state.status === 'loading' ? 'loading' : ''}`;
  if (state.status === 'error' && !state.feed) {
    count.innerHTML = 'Connection error';
  } else if (state.status === 'loading' && !state.feed) {
    count.innerHTML = 'Loading…';
  } else {
    const n = state.feed?.quakes.length ?? 0;
    count.innerHTML = `<strong>${num(n)}</strong> quakes · updated ${relTime(state.lastFetched)}`;
  }
}

async function renderView(): Promise<void> {
  const root = document.getElementById('view-root');
  if (!root) return;
  if (state.status === 'error' && !state.feed) {
    root.innerHTML = `<div class="state-msg"><div class="big">Couldn’t reach the USGS feed</div>${escState(state.error)}<div style="margin-top:1rem"><button class="pill-btn active" id="retry">Try again</button></div></div>`;
    document.getElementById('retry')?.addEventListener('click', () => loadData(true));
    return;
  }
  if (!state.feed) {
    root.innerHTML = `<div class="grid cols-auto"><div class="stat-tile skeleton" style="height:96px"></div><div class="stat-tile skeleton" style="height:96px"></div><div class="stat-tile skeleton" style="height:96px"></div><div class="stat-tile skeleton" style="height:96px"></div></div><div class="panel skeleton" style="height:300px;margin-top:1rem"></div>`;
    return;
  }
  const view = VIEWS[state.view] ?? VIEWS.overview;
  await view.render(root);
}

function setView(id: string): void {
  if (!VIEWS[id]) id = 'overview';
  state.view = id;
  document.querySelectorAll('#nav-tabs button').forEach((b) => b.classList.toggle('active', b.getAttribute('data-view') === id));
  if (location.hash !== `#${id}` && !location.hash.startsWith('#event=')) history.replaceState(null, '', `#${id}`);
  void renderView();
}

async function loadData(force = false): Promise<void> {
  if (controller) controller.abort();
  controller = new AbortController();
  if (!state.feed || force) { state.status = 'loading'; updateStatus(); if (force) { state.feed = null; void renderView(); } }
  try {
    const feed = await fetchFeed({ period: state.period, band: state.band }, controller.signal);
    state.feed = feed;
    state.status = 'ok';
    state.error = '';
    state.lastFetched = Date.now();
    state.plateCache.clear();
    updateStatus();
    await renderView();
    handleDeepLink();
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    state.status = 'error';
    state.error = (err as Error).message || 'Network error';
    updateStatus();
    if (!state.feed) void renderView();
  }
}

let deepLinkHandled = false;
function handleDeepLink(): void {
  if (deepLinkHandled) return;
  const h = location.hash;
  if (h.startsWith('#event=')) {
    const id = decodeURIComponent(h.slice(7));
    const q = state.feed?.quakes.find((x) => x.id === id);
    if (q) { deepLinkHandled = true; openQuakeDrawer(q); }
  }
}

function wire(): void {
  document.getElementById('seg-period')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-period]');
    if (!btn) return;
    state.period = btn.getAttribute('data-period') as Period;
    savePrefs();
    document.querySelectorAll('#seg-period button').forEach((b) => b.classList.toggle('active', b === btn));
    loadData(true);
  });
  document.getElementById('seg-band')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-band]');
    if (!btn) return;
    state.band = btn.getAttribute('data-band') as MagBand;
    savePrefs();
    document.querySelectorAll('#seg-band button').forEach((b) => b.classList.toggle('active', b === btn));
    loadData(true);
  });
  document.getElementById('nav-tabs')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) setView(btn.getAttribute('data-view')!);
  });
  document.getElementById('btn-about')?.addEventListener('click', () => openAbout());
  document.getElementById('btn-refresh')?.addEventListener('click', () => loadData(false));
}

function escState(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] ?? c));
}

function boot(): void {
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = shell();
  initTooltip();
  initOverlays();
  wire();

  // Initial view from hash.
  const h = location.hash.replace('#', '');
  if (VIEWS[h]) state.view = h;
  document.querySelectorAll('#nav-tabs button').forEach((b) => b.classList.toggle('active', b.getAttribute('data-view') === state.view));

  // Load plate polygons for classification (non-blocking).
  fetch('data/plates.geojson').then((r) => (r.ok ? r.json() : null)).then((geo) => {
    if (geo) { state.plates = parsePlates(geo); state.plateCache.clear(); if (state.feed) void renderView(); }
  }).catch(() => { /* classification falls back to coarse regions */ });

  loadData(true);
  pollTimer = setInterval(() => { if (document.visibilityState === 'visible') loadData(false); }, 60_000);
  setInterval(updateStatus, 1000);
  window.addEventListener('hashchange', () => {
    const hv = location.hash.replace('#', '');
    if (VIEWS[hv] && hv !== state.view) setView(hv);
  });
}

boot();
void pollTimer;
